import React, { useMemo } from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { Controller } from 'react-hook-form';
import PersonIcon from '@mui/icons-material/Person';
import styled from 'styled-components';
import ProfileAvatar from '../../../common/ProfileAvatar';

import Input from '../../../common/form/Input';
import MaskInput from '../../../common/form/MaskInput';
import Select from '../../../common/form/Select';
import AutoComplete from '../../../common/form/AutoComplete';
import { FormValues } from './types';
import { CrewRolesDDOptions } from '../../../../data/models/user';
import { UserGroup } from '../../../../data/models/userGroup';
import { CompanyExternalUser } from '../../../../data/models/companyExternalUser';

interface OverviewStepProps {
	formValues: FormValues;
	setValue: any;
	control: any;
	errors: any;
	user?: CompanyExternalUser;
	userGroups: UserGroup[];
	handleConvert: () => void;
	isRequired?: boolean;
	onProfilePhotoDrop?: (acceptedFiles: File[]) => void;
}

export default function OverviewStep({
	formValues,
	setValue,
	control,
	errors,
	user,
	userGroups,
	handleConvert,
	isRequired = true,
	onProfilePhotoDrop,
}: OverviewStepProps) {
	// Convert user groups to AutoComplete options format
	const groupOptions = useMemo(() => {
		return userGroups.map((group) => ({
			id: group.id,
			display: group.name,
		}));
	}, [userGroups]);

	// Convert selected groups from form values to AutoComplete format
	const selectedGroupOptions = useMemo(() => {
		if (!formValues.groups || !Array.isArray(formValues.groups)) {
			return [];
		}
		return formValues.groups.map((groupId) => {
			const group = userGroups.find((g) => g.id === groupId);
			return {
				id: groupId,
				display: group?.name || groupId,
			};
		});
	}, [formValues.groups, userGroups]);

	const handleGroupsChange = (event: any, value: any) => {
		// Extract just the IDs
		const groupIds = value.map((option: any) => option.id);
		setValue('groups', groupIds, { shouldDirty: true });
	};

	return (
		<Box sx={{ py: 2 }}>
			{/* Avatar Section */}
			<ProfileAvatar
				profilePictureUrl={formValues.profilePictureUrl}
				firstName={formValues.firstName}
				lastName={formValues.lastName}
				onDrop={onProfilePhotoDrop}
			/>

			<Grid container spacing={2}>
				{/* First Name and Last Name side by side */}
				<Grid item xs={6}>
					<Controller
						name="firstName"
						control={control}
						rules={
							isRequired
								? { required: 'First name is required' }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Firstname"
								required={isRequired}
								fullWidth
								error={!!errors.firstName}
								helperText={errors.firstName?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={6}>
					<Controller
						name="lastName"
						control={control}
						rules={
							isRequired
								? { required: 'Last name is required' }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Lastname"
								required={isRequired}
								fullWidth
								error={!!errors.lastName}
								helperText={errors.lastName?.message}
							/>
						)}
					/>
				</Grid>

				{/* Email */}
				<Grid item xs={12}>
					<Controller
						name="email"
						control={control}
						rules={
							isRequired
								? {
										required: 'Email is required',
										pattern: {
											value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
											message:
												'Please enter a valid email address',
										},
								  }
								: {
										pattern: {
											value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
											message:
												'Please enter a valid email address',
										},
								  }
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Email"
								type="email"
								required={isRequired}
								fullWidth
								error={!!errors.email}
								helperText={errors.email?.message}
							/>
						)}
					/>
				</Grid>

				{/* Phone Number */}
				<Grid item xs={12}>
					<Controller
						name="phone"
						control={control}
						render={({ field }) => (
							<MaskInput
								{...field}
								id="phone"
								name="phone"
								label="Phone Number"
								mask="phone"
								fullWidth
								error={!!errors.phone}
								helperText={errors.phone?.message}
							/>
						)}
					/>
				</Grid>

				{/* Role */}
				<Grid item xs={12}>
					<Controller
						name="role"
						control={control}
						render={({ field }) => (
							<Select
								value={field.value || ''}
								onChange={(event) => {
									field.onChange(event.target.value);
								}}
								label="Role"
								options={CrewRolesDDOptions}
								fullWidth
							/>
						)}
					/>
				</Grid>

				{/* Groups */}
				<Grid item xs={12}>
					<Controller
						name="groups"
						control={control}
						render={({ field }) => (
							<AutoComplete
								id="groups"
								label="Groups"
								options={groupOptions}
								value={selectedGroupOptions}
								onChange={handleGroupsChange}
								multiple={true}
								placeholder="Select groups..."
								error={!!errors.groups}
								helperText={errors.groups?.message}
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Convert to Internal */}
			<Box sx={{ mt: 4 }}>
				<ConvertLink onClick={handleConvert}>
					CONVERT TO INTERNAL
				</ConvertLink>
			</Box>
		</Box>
	);
}

const ConvertLink = styled(Typography)`
	color: #1976d2;
	cursor: pointer;
	font-weight: 500;
	font-size: 0.875rem;
	text-transform: uppercase;

	&:hover {
		text-decoration: underline;
	}
`;
